'use client';

import {
  Salutation,
  isSalutation,
} from '@schuelerhilfe/shared/src/enums/salutation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { z } from 'zod';
import { Checkbox } from '@/components/form/Checkbox';
import { TextareaField } from '@/components/form/TextareaField';
import { TextField } from '@/components/form/TextField';
import { generateFormCustomerId } from '@/lib/form/customerId';
import { toMessageOverrides } from '@/lib/form/fieldTexts';
import { useAppForm } from '@/lib/form/hook';
import { createFormSchemas } from '@/lib/form/schemas';
import type {
  ContactFormData,
  ContactFormProps,
  ContactFormSubmitData,
  FormFieldTextField,
} from '@/lib/form/types';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

const CONTACT_FORM_DEFAULTS: ContactFormData = {
  salutation: '',
  firstname: '',
  lastname: '',
  phone: '',
  email: '',
  message: '',
  consent: false,
  subject: '',
};

export function ContactForm({
  onSubmit,
  children,
  extraFields = [],
  extraContent,
  className,
  compact,
  subjectOptions,
  salutationOptions: salutationOptionsProp,
  centerNumber,
  fieldTexts,
}: ContactFormProps) {
  const tCommon = useTranslations('common');
  const tForm = useTranslations('form');
  const tValidation = useTranslations('validation');
  const schemas = createFormSchemas(
    tValidation,
    toMessageOverrides(fieldTexts),
  );
  // CMS label override per field; the label doubles as the visible
  // help/placeholder text in this fixed-field design.
  const fieldLabel = (field: FormFieldTextField, translationKey: string) =>
    fieldTexts?.[field]?.label ?? tForm(translationKey);

  const salutationOptions = salutationOptionsProp ?? [
    { value: Salutation.Mr, label: tForm('mr') },
    { value: Salutation.Mrs, label: tForm('mrs') },
  ];

  const activeExtraNames = new Set(extraFields.map((f) => f.name));

  const location = useOptionalRenderContext()?.entities.location;
  // An explicit `centerNumber` prop takes priority over the render context's
  // location — needed by callers (e.g. the header's ContactModal) that
  // render outside `<RenderContextProvider>` but already know the current
  // location's center number some other way (see AM-SH-378).
  const resolvedCenterNumber = centerNumber ?? location?.centerNumber;
  // Lazy initializer: computed once per mount, stable across re-renders of
  // this form instance (see AM-SH-378). Its random suffix is stable for the
  // whole browser tab, so every form the visitor meets shares one.
  const [customerId] = useState(() =>
    generateFormCustomerId(resolvedCenterNumber),
  );

  const form = useAppForm({
    defaultValues: CONTACT_FORM_DEFAULTS,
    onSubmit: async ({ value }) => {
      const baseShape: Record<string, z.ZodType> = {
        salutation: schemas.salutationSchema,
        firstname: schemas.firstnameSchema,
        lastname: schemas.lastnameSchema,
        phone: schemas.phoneSchema,
        email: schemas.emailSchema,
      };
      if (subjectOptions) {
        baseShape.subject = z.string().min(1);
      }
      for (const field of extraFields) {
        baseShape[field.name] = field.validator;
      }
      const parsed = z.object(baseShape).parse(value);

      const salVal = parsed.salutation as string;
      const result: ContactFormSubmitData = {
        salutation: isSalutation(salVal) ? salVal : Salutation.None,
        firstname: parsed.firstname as string,
        lastname: parsed.lastname as string,
        phone: parsed.phone as string,
        email: parsed.email as string,
        customerNumber: customerId,
      };
      if (parsed.subject) {
        result.subject = parsed.subject as string;
      }
      if (activeExtraNames.has('message') && parsed.message) {
        result.message = parsed.message as string;
      }
      if (activeExtraNames.has('consent')) {
        result.consent = parsed.consent as true;
      }
      await onSubmit(result);
    },
  });

  if (compact) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className={className}
        noValidate
      >
        <input
          type="hidden"
          className="form-customerId"
          value={customerId}
          readOnly
        />
        <div className="mb-3">
          <form.AppField
            name="salutation"
            validators={{
              onChange: schemas.salutationSchema,
              onSubmit: schemas.salutationSchema,
            }}
          >
            {(field) => (
              <field.RadioGroup options={salutationOptions} required />
            )}
          </form.AppField>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <form.AppField
            name="firstname"
            validators={{
              onBlur: schemas.firstnameSchema,
              onSubmit: schemas.firstnameSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={fieldLabel('firstname', 'firstname')}
                autoComplete="given-name"
                required
              />
            )}
          </form.AppField>
          <form.AppField
            name="lastname"
            validators={{
              onBlur: schemas.lastnameSchema,
              onSubmit: schemas.lastnameSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={fieldLabel('lastname', 'lastname')}
                autoComplete="family-name"
                required
              />
            )}
          </form.AppField>
        </div>

        <div className="mb-3">
          <form.AppField
            name="phone"
            validators={{
              onBlur: schemas.phoneSchema,
              onSubmit: schemas.phoneSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={fieldLabel('phone', 'phone')}
                type="tel"
                autoComplete="tel"
                required
              />
            )}
          </form.AppField>
        </div>

        <div className="mb-3">
          <form.AppField
            name="email"
            validators={{
              onBlur: schemas.emailSchema,
              onSubmit: schemas.emailSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={fieldLabel('email', 'email')}
                type="email"
                autoComplete="email"
                required
              />
            )}
          </form.AppField>
        </div>

        {subjectOptions && (
          <div className="mb-3">
            <form.AppField name="subject">
              {(field) => (
                <field.SelectField
                  label={fieldLabel('subject', 'subjectLabel')}
                  options={subjectOptions}
                />
              )}
            </form.AppField>
          </div>
        )}

        {(extraFields.length > 0 || extraContent) && (
          <div className="mb-3 space-y-3">
            {extraContent}
            {extraFields.map((ef) => (
              <div key={ef.name}>
                <form.AppField
                  name={ef.name}
                  validators={{
                    onChange: ef.validator as never,
                    onSubmit: ef.validator as never,
                  }}
                >
                  {() => {
                    switch (ef.component) {
                      case 'TextField':
                        return (
                          <TextField {...(ef.props as { label: string })} />
                        );
                      case 'TextareaField':
                        return (
                          <TextareaField {...(ef.props as { label: string })} />
                        );
                      case 'Checkbox':
                        return (
                          <Checkbox>
                            {ef.props.children as React.ReactNode}
                          </Checkbox>
                        );
                    }
                  }}
                </form.AppField>
              </div>
            ))}
          </div>
        )}

        <form.Subscribe
          selector={(s) => [s.canSubmit, s.isSubmitting] as const}
        >
          {([canSubmit, isSubmitting]) =>
            children({
              handleSubmit: () => form.handleSubmit(),
              canSubmit,
              isSubmitting,
            })
          }
        </form.Subscribe>
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className={className}
      noValidate
    >
      <input
        type="hidden"
        className="form-customerId"
        value={customerId}
        readOnly
      />
      <div className="mb-4">
        <form.AppField
          name="salutation"
          validators={{
            onChange: schemas.salutationSchema,
            onSubmit: schemas.salutationSchema,
          }}
        >
          {(field) => <field.RadioGroup options={salutationOptions} required />}
        </form.AppField>
      </div>

      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <form.AppField
          name="firstname"
          validators={{
            onBlur: schemas.firstnameSchema,
            onSubmit: schemas.firstnameSchema,
          }}
        >
          {(field) => (
            <field.TextField
              label={fieldLabel('firstname', 'firstname')}
              autoComplete="given-name"
              required
            />
          )}
        </form.AppField>
        <form.AppField
          name="lastname"
          validators={{
            onBlur: schemas.lastnameSchema,
            onSubmit: schemas.lastnameSchema,
          }}
        >
          {(field) => (
            <field.TextField
              label={fieldLabel('lastname', 'lastname')}
              autoComplete="family-name"
              required
            />
          )}
        </form.AppField>
      </div>

      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <form.AppField
          name="phone"
          validators={{
            onBlur: schemas.phoneSchema,
            onSubmit: schemas.phoneSchema,
          }}
        >
          {(field) => (
            <field.TextField
              label={fieldLabel('phone', 'phone')}
              type="tel"
              autoComplete="tel"
              required
            />
          )}
        </form.AppField>
        <form.AppField
          name="email"
          validators={{
            onBlur: schemas.emailSchema,
            onSubmit: schemas.emailSchema,
          }}
        >
          {(field) => (
            <field.TextField
              label={fieldLabel('email', 'email')}
              type="email"
              autoComplete="email"
              required
            />
          )}
        </form.AppField>
      </div>

      {subjectOptions && (
        <div className="mb-4">
          <form.AppField name="subject">
            {(field) => (
              <field.SelectField
                label={fieldLabel('subject', 'subjectLabel')}
                options={subjectOptions}
              />
            )}
          </form.AppField>
        </div>
      )}

      {(extraFields.length > 0 || extraContent) && (
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          {extraContent}
          {extraFields.map((ef) => (
            <div key={ef.name} className="sm:col-span-2">
              <form.AppField
                name={ef.name}
                validators={{
                  onChange: ef.validator as never,
                  onSubmit: ef.validator as never,
                }}
              >
                {() => {
                  switch (ef.component) {
                    case 'TextField':
                      return <TextField {...(ef.props as { label: string })} />;
                    case 'TextareaField':
                      return (
                        <TextareaField {...(ef.props as { label: string })} />
                      );
                    case 'Checkbox':
                      return (
                        <Checkbox>
                          {ef.props.children as React.ReactNode}
                        </Checkbox>
                      );
                  }
                }}
              </form.AppField>
            </div>
          ))}
        </div>
      )}

      <p className="mt-4 mb-4 text-sm">{tCommon('requiredFields')}</p>

      <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
        {([canSubmit, isSubmitting]) =>
          children({
            handleSubmit: () => form.handleSubmit(),
            canSubmit,
            isSubmitting,
          })
        }
      </form.Subscribe>
    </form>
  );
}
